import { X, EyeOff } from 'lucide-react';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ReleaseNote {
  date: string;
  title: string;
  description: string;
  version?: string; // Add optional version field
}

interface ReleaseNotesProps {
  notes: ReleaseNote[];
  onClose: () => void;
  onHideLatest: () => void;
  releaseNotesHiddenDate: string | null;
}

const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ notes, onClose, onHideLatest, releaseNotesHiddenDate }) => {
  const hiddenDate = releaseNotesHiddenDate ? new Date(releaseNotesHiddenDate) : null;

  // Filter notes to only show those newer than hiddenDate
  const filteredNotes = notes.filter((note) => {
    const noteDate = new Date(note.date);
    return !hiddenDate || noteDate > hiddenDate;
  });

  // Sort in descending order (newest first)
  const sortedNotes = [...filteredNotes].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b shrink-0">
          <CardTitle className="text-2xl font-lighter">Release Notes</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <ScrollArea className="flex-1 overflow-auto">
          <CardContent className="p-6 space-y-6">
            {sortedNotes.map((note, index) => (
              <div key={index} className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-primary flex justify-between items-center">
                    <span>{note.title}</span>
                    {note.version && (
                      <span className="text-xs px-2 py-1 bg-primary/10 rounded-full text-primary">
                        v{note.version}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    <strong>Date:</strong> {new Date(note.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{note.description}</p>
                {index < sortedNotes.length - 1 && <hr className="border-t border-gray-200" />}
              </div>
            ))}
          </CardContent>
        </ScrollArea>
        <div className="p-4 border-t flex justify-end space-x-2 shrink-0">
          <Button variant="outline" onClick={onHideLatest}>
            <EyeOff className="mr-2 h-4 w-4" />
            Dismiss
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  );
};

export default ReleaseNotes;